import React, { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowUpDown, Trophy, TrendingUp, TrendingDown, Target, Layers } from 'lucide-react';
import type { SavedComparison, ComparisonRow } from '@/services/strategyService';
import { num } from '@/lib/format';

const money = (v: number | null | undefined) => {
  if (v == null) return '—';
  const a = Math.abs(v);
  const s = a >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toFixed(0);
  return `$${s}`;
};

type SortKey = 'strategyName' | 'assetClass' | 'timeframe' | 'tradeCount' | 'totalPnL'
  | 'winRate' | 'profitFactor' | 'avgRMultiple' | 'maxDrawdownPercent' | 'pctResolvedByExit'
  | 'sharpeRatio' | 'recoveryFactor' | 'lrCorrelation';

interface Props {
  comparison: SavedComparison;
  /** Owner drill-down — open a run's detail. Undefined ⇒ read-only (public) scoreboard. */
  onRowClick?: (runId: string) => void;
}

/**
 * Information-rich comparison dashboard: headline KPIs + comparative charts (net-PnL bars,
 * risk/reward scatter) + a sortable ranking table. Renders entirely from the saved
 * ComparisonRow snapshot — no per-run fetch — so it's instant and works in the public
 * (read-only) view. Rows drill into the run detail when onRowClick is supplied.
 */
export const ComparisonScoreboard: React.FC<Props> = ({ comparison, onRowClick }) => {
  const rows = comparison.rows ?? [];
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'totalPnL', dir: 'desc' });

  // ── Headline KPIs ──
  const kpis = useMemo(() => {
    const withPnl = rows.filter(r => r.totalPnL != null);
    const totalPnL = withPnl.reduce((s, r) => s + (r.totalPnL ?? 0), 0);
    const profitable = withPnl.filter(r => (r.totalPnL ?? 0) > 0).length;
    const winRates = rows.filter(r => r.winRate != null).map(r => r.winRate as number);
    const avgWin = winRates.length ? winRates.reduce((a, b) => a + b, 0) / winRates.length : null;
    const best = withPnl.length ? withPnl.reduce((a, b) => ((a.totalPnL ?? 0) >= (b.totalPnL ?? 0) ? a : b)) : null;
    const worst = withPnl.length ? withPnl.reduce((a, b) => ((a.totalPnL ?? 0) <= (b.totalPnL ?? 0) ? a : b)) : null;
    const totalTrades = rows.reduce((s, r) => s + (r.tradeCount ?? 0), 0);
    return { totalPnL, profitable, avgWin, best, worst, totalTrades, count: rows.length };
  }, [rows]);

  // ── Sorted table ──
  const sorted = useMemo(() => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (sort.key === 'timeframe') {
        const tfm: Record<string, number> = { '1m': 1, '15m': 15, '30m': 30, '1h': 60, '4h': 240, '1D': 1440 };
        return ((tfm[a.timeframe] ?? 0) - (tfm[b.timeframe] ?? 0)) * dir;
      }
      const av = (a as any)[sort.key], bv = (b as any)[sort.key];
      if (typeof av === 'string' || typeof bv === 'string')
        return String(av ?? '').localeCompare(String(bv ?? '')) * dir;
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (av - bv) * dir;
    });
  }, [rows, sort]);

  // ── Net-PnL bar chart (sorted desc, symmetric scale around 0) ──
  const pnlBars = useMemo(() => {
    const items = rows.filter(r => r.totalPnL != null)
      .map(r => ({ label: `${r.strategyName} · ${r.timeframe}`, value: r.totalPnL as number }))
      .sort((a, b) => b.value - a.value);
    const maxAbs = Math.max(1, ...items.map(i => Math.abs(i.value)));
    return { items, maxAbs };
  }, [rows]);

  // ── Risk/reward scatter: x = max drawdown %, y = net PnL, bubble ~ trades ──
  const scatter = useMemo(() => {
    const pts = rows.filter(r => r.totalPnL != null && r.maxDrawdownPercent != null)
      .map(r => ({ dd: Math.abs(r.maxDrawdownPercent as number), pnl: r.totalPnL as number, trades: r.tradeCount, name: `${r.strategyName} · ${r.timeframe}` }));
    const maxDd = Math.max(0.01, ...pts.map(p => p.dd));
    const maxPnl = Math.max(1, ...pts.map(p => Math.abs(p.pnl)));
    const maxTrades = Math.max(1, ...pts.map(p => p.trades));
    return { pts, maxDd, maxPnl, maxTrades };
  }, [rows]);

  const setSortKey = (key: SortKey) =>
    setSort(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));
  const th = (key: SortKey, label: string, cls = '') => (
    <th className={`px-3 py-2 cursor-pointer select-none hover:text-foreground ${cls}`} onClick={() => setSortKey(key)}>
      <span className="inline-flex items-center gap-1">{label}<ArrowUpDown className={`h-3 w-3 ${sort.key === key ? 'opacity-80' : 'opacity-30'}`} /></span>
    </th>
  );

  const Kpi: React.FC<{ icon: React.ReactNode; label: string; value: string; sub?: string; tone?: 'pos' | 'neg' | 'neutral' }> =
    ({ icon, label, value, sub, tone = 'neutral' }) => (
      <Card>
        <CardContent className="p-3">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">{icon}{label}</div>
          <div className={`text-xl font-semibold tabular-nums mt-0.5 ${tone === 'pos' ? 'text-emerald-500' : tone === 'neg' ? 'text-red-500' : ''}`}>{value}</div>
          {sub && <div className="text-[11px] text-muted-foreground truncate" title={sub}>{sub}</div>}
        </CardContent>
      </Card>
    );

  return (
    <div className="space-y-4">
      {/* Params line */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span><Layers className="h-3 w-3 inline mr-1" />{kpis.count} runs</span>
        <span>· {comparison.lookbackDays}d lookback</span>
        {comparison.symbolListName && <span>· universe: {comparison.symbolListName}</span>}
        {comparison.accountEquity != null && <span>· account ${num(comparison.accountEquity, 0)}</span>}
        {comparison.timeframes?.length > 0 && <span>· TFs: {comparison.timeframes.join(', ')}</span>}
        {comparison.endDate && <span>· ending {comparison.endDate}</span>}
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <Kpi icon={<Trophy className="h-3.5 w-3.5" />} label="Best strategy"
          value={kpis.best ? money(kpis.best.totalPnL) : '—'} tone={kpis.best && (kpis.best.totalPnL ?? 0) >= 0 ? 'pos' : 'neg'}
          sub={kpis.best ? `${kpis.best.strategyName} · ${kpis.best.timeframe}` : undefined} />
        <Kpi icon={<TrendingUp className="h-3.5 w-3.5" />} label="Total net PnL"
          value={money(kpis.totalPnL)} tone={kpis.totalPnL >= 0 ? 'pos' : 'neg'} />
        <Kpi icon={<Target className="h-3.5 w-3.5" />} label="Profitable"
          value={`${kpis.profitable}/${kpis.count}`} sub={kpis.count ? `${((kpis.profitable / kpis.count) * 100).toFixed(0)}% of runs` : undefined} />
        <Kpi icon={<Target className="h-3.5 w-3.5" />} label="Avg win rate"
          value={kpis.avgWin != null ? `${num(kpis.avgWin, 1)}%` : '—'} />
        <Kpi icon={<TrendingDown className="h-3.5 w-3.5" />} label="Worst strategy"
          value={kpis.worst ? money(kpis.worst.totalPnL) : '—'} tone={kpis.worst && (kpis.worst.totalPnL ?? 0) >= 0 ? 'pos' : 'neg'}
          sub={kpis.worst ? `${kpis.worst.strategyName} · ${kpis.worst.timeframe}` : undefined} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Net PnL by strategy */}
        <Card>
          <CardContent className="p-3">
            <div className="text-sm font-medium mb-2">Net PnL by strategy</div>
            {pnlBars.items.length === 0 ? <div className="text-xs text-muted-foreground">No PnL data.</div> : (
              <div className="space-y-1 max-h-[260px] overflow-y-auto pr-1">
                {pnlBars.items.map((it, i) => {
                  const w = (Math.abs(it.value) / pnlBars.maxAbs) * 50; // half-width: bars grow from the centre
                  const pos = it.value >= 0;
                  return (
                    <div key={i} className="flex items-center gap-2 text-[11px]">
                      <div className="w-32 truncate text-muted-foreground" title={it.label}>{it.label}</div>
                      <div className="flex-1 h-4 relative bg-muted/30 rounded">
                        <div className="absolute top-0 bottom-0 left-1/2 w-px bg-border" />
                        <div className={`absolute top-0 bottom-0 ${pos ? 'bg-emerald-500/70' : 'bg-red-500/70'} rounded`}
                          style={pos ? { left: '50%', width: `${w}%` } : { right: '50%', width: `${w}%` }} />
                      </div>
                      <div className={`w-14 text-right tabular-nums ${pos ? 'text-emerald-500' : 'text-red-500'}`}>{money(it.value)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Risk/reward scatter */}
        <Card>
          <CardContent className="p-3">
            <div className="text-sm font-medium mb-2">Return vs drawdown <span className="text-[11px] text-muted-foreground font-normal">(top-left = best)</span></div>
            {scatter.pts.length === 0 ? <div className="text-xs text-muted-foreground">No drawdown data.</div> : (
              <div className="relative" style={{ height: 260 }}>
                <svg viewBox="-6 -4 112 112" className="w-full h-full">
                  {/* zero PnL line */}
                  {(() => { const y0 = 54 - (0 / scatter.maxPnl) * 50; return <line x1="0" x2="100" y1={y0} y2={y0} stroke="currentColor" strokeOpacity="0.15" strokeWidth="0.3" />; })()}
                  <line x1="0" x2="0" y1="0" y2="104" stroke="currentColor" strokeOpacity="0.15" strokeWidth="0.3" />
                  {scatter.pts.map((p, i) => {
                    const cx = (p.dd / scatter.maxDd) * 100;
                    const cy = 54 - (p.pnl / scatter.maxPnl) * 50;   // centre band at y=54, ±50
                    const r = 1.5 + Math.sqrt(p.trades / scatter.maxTrades) * 4;
                    const color = p.pnl >= 0 ? '#22c55e' : '#ef4444';
                    return (
                      <g key={i}>
                        <circle cx={cx} cy={cy} r={r} fill={color} opacity={0.55} />
                        <circle cx={cx} cy={cy} r={Math.max(r, 3)} fill="transparent">
                          <title>{`${p.name}\nPnL ${money(p.pnl)} · maxDD ${p.dd.toFixed(1)}% · ${p.trades} trades`}</title>
                        </circle>
                      </g>
                    );
                  })}
                </svg>
                <div className="absolute bottom-0 left-0 right-0 text-center text-[9px] text-muted-foreground">max drawdown % →</div>
                <div className="absolute top-1/2 -left-1 -translate-y-1/2 -rotate-90 text-[9px] text-muted-foreground origin-center">net PnL</div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Ranking table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground border-b bg-muted/30">
              <tr>
                {th('strategyName', 'Strategy')}
                {th('assetClass', 'Asset')}
                {th('timeframe', 'TF')}
                {th('tradeCount', 'Trades', 'text-right')}
                {th('totalPnL', 'Net PnL', 'text-right')}
                {th('winRate', 'Win%', 'text-right')}
                {th('profitFactor', 'PF', 'text-right')}
                {th('avgRMultiple', 'Avg R', 'text-right')}
                {th('maxDrawdownPercent', 'Max DD%', 'text-right')}
                {th('sharpeRatio', 'Sharpe', 'text-right')}
                {th('recoveryFactor', 'Recov', 'text-right')}
                {th('lrCorrelation', 'LR fit', 'text-right')}
                {th('pctResolvedByExit', 'Resolv%', 'text-right')}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r: ComparisonRow, i) => (
                <tr key={`${r.runId}_${i}`}
                  className={`border-b border-border/40 ${onRowClick ? 'hover:bg-muted/30 cursor-pointer' : ''}`}
                  role={onRowClick ? 'button' : undefined} tabIndex={onRowClick ? 0 : undefined}
                  onClick={onRowClick ? () => onRowClick(r.runId) : undefined}
                  onKeyDown={onRowClick ? e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(r.runId); } } : undefined}
                  title={onRowClick ? "Open this run's detail" : undefined}>
                  <td className="px-3 py-1.5 max-w-[240px] truncate" title={r.strategyName}>{r.strategyName}</td>
                  <td className="px-3 py-1.5"><Badge variant="outline" className="text-[10px]">{r.assetClass}</Badge></td>
                  <td className="px-3 py-1.5">{r.timeframe}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{r.tradeCount}</td>
                  <td className={`px-3 py-1.5 text-right tabular-nums ${(r.totalPnL ?? 0) > 0 ? 'text-emerald-500' : (r.totalPnL ?? 0) < 0 ? 'text-red-500' : ''}`}>{num(r.totalPnL, 0)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{num(r.winRate, 1)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{num(r.profitFactor, 2)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{num(r.avgRMultiple, 2)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{num(r.maxDrawdownPercent, 1)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{num(r.sharpeRatio, 2)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{num(r.recoveryFactor, 2)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{num(r.lrCorrelation, 2)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{num(r.pctResolvedByExit, 0)}</td>
                </tr>
              ))}
              {sorted.length === 0 && <tr><td colSpan={13} className="px-3 py-4 text-center text-muted-foreground">No rows in this comparison.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

export default ComparisonScoreboard;
